import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Search, X } from 'lucide-react';
import {
  MERGE_PROJECTS_MAX,
  PROJECT_TRASH_DAYS,
  schemas,
  type ClientDetails,
  type ProjectListItem,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorState } from '@/components/feedback';
import { Form, TextField, FormErrorBanner } from '@/components/form';
import { cn } from '@/lib/cn';
import { ClientCreateDialog } from '@/features/clients/components/ClientCreateDialog';
import { useMergeProjects, useProjects } from '@/features/projects/hooks';
import { errorKeyFor } from '@/features/projects/errorMessages';
import { toListQuery } from '@/features/projects/search';
import { projectStatusBadgeClass } from '@/features/projects/statusBadge';
import { projectStatusLabel } from '@/features/projects/statusLabel';
import { roomsApi } from '@/features/projects/rooms/api';
import { roomsQueryKeys } from '@/features/projects/rooms/hooks';
import { ClientSearchField } from './ClientSearchField';

const LIST_PAGE_SIZE = 100;

const formSchema = schemas.mergeProjectsRequest.pick({ clientId: true, title: true });
type FormValues = { clientId: string; title: string };

interface ProjectMergeDialogProps {
  /** The project whose row started this, already ticked. Null keeps the dialog closed. */
  seed: ProjectListItem | null;
  onClose: () => void;
}

/**
 * "Comprimă proiecte", in two steps: pick the projects, then say whose the result
 * is and what it is called. Mounted only while open, so every opening starts from
 * its own seed with nothing left over from the last one.
 */
export function ProjectMergeDialog({ seed, onClose }: ProjectMergeDialogProps) {
  return seed ? <MergeFlow seed={seed} onClose={onClose} /> : null;
}

function MergeFlow({ seed, onClose }: { seed: ProjectListItem; onClose: () => void }) {
  const merge = useMergeProjects();
  // Whole rows rather than ids: a pick has to outlive the search that found it,
  // and the second step needs the titles of projects the list may no longer show.
  const [selected, setSelected] = useState<ProjectListItem[]>([seed]);
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  const closeIfIdle = () => {
    if (!merge.isPending && !clientDialogOpen) onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) closeIfIdle();
      }}
    >
      <DialogContent
        className="w-[min(94vw,640px)] max-w-none p-0"
        // Ticking boxes and searching is nothing to ask about losing.
        unsavedChanges={step === 'select' ? false : undefined}
      >
        {step === 'select' ? (
          <SelectStep
            seed={seed}
            selected={selected}
            onChange={setSelected}
            onCancel={closeIfIdle}
            onNext={() => {
              merge.reset();
              setStep('details');
            }}
          />
        ) : (
          <DetailsStep
            projects={selected}
            merge={merge}
            clientDialogOpen={clientDialogOpen}
            onClientDialogOpenChange={setClientDialogOpen}
            onBack={() => setStep('select')}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SelectStep({
  seed,
  selected,
  onChange,
  onCancel,
  onNext,
}: {
  seed: ProjectListItem;
  selected: ProjectListItem[];
  onChange: (next: ProjectListItem[]) => void;
  onCancel: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError, error, refetch } = useProjects(
    toListQuery({ pageSize: LIST_PAGE_SIZE, search: debouncedSearch || undefined }),
  );

  // The seed's client first: what gets merged is usually one client's projects.
  // Sorted by the seed rather than the selection, so ticking never moves a row.
  const projects = useMemo(
    () =>
      [...(data?.items ?? [])].sort(
        (a, b) => Number(b.clientId === seed.clientId) - Number(a.clientId === seed.clientId),
      ),
    [data?.items, seed.clientId],
  );

  const selectedIds = new Set(selected.map((project) => project.id));
  const atCap = selected.length >= MERGE_PROJECTS_MAX;
  const canContinue = selected.length >= 2;
  const toggle = (project: ProjectListItem) =>
    onChange(
      selectedIds.has(project.id)
        ? selected.filter((item) => item.id !== project.id)
        : [...selected, project],
    );

  return (
    <>
      <DialogHeader className="mb-0 border-b border-border px-6 py-5">
        <DialogTitle className="text-xl font-semibold">{t('projects.merge.action')}</DialogTitle>
        <DialogDescription>{t('projects.merge.selectDescription')}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 px-6 py-5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
            placeholder={t('projects.filters.searchPlaceholder') ?? undefined}
            aria-label={t('projects.filters.searchPlaceholder') ?? undefined}
            className="pl-9"
          />
        </div>

        {selected.length > 0 && (
          <div>
            <div className="mb-2 text-sm font-semibold">
              {t('projects.merge.selected', { count: selected.length })}
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.map((project) => (
                <span
                  key={project.id}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-1 pl-3 pr-1 text-sm"
                >
                  <span className="truncate">{project.title}</span>
                  <button
                    type="button"
                    onClick={() => toggle(project)}
                    aria-label={t('projects.merge.removeSelected', { title: project.title })}
                    className="rounded-full p-1 hover:bg-background"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-border p-2">
            {isLoading ? (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('common.loading')}</p>
            ) : projects.length === 0 ? (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('projects.empty')}</p>
            ) : (
              projects.map((project) => {
                const checked = selectedIds.has(project.id);
                const disabled = project.inScheduler || (!checked && atCap);
                return (
                  <label
                    key={project.id}
                    className={cn(
                      'flex items-start gap-3 rounded-lg px-2 py-1.5 text-sm',
                      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(project)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{project.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {project.clientName}
                        {' · '}
                        {t('projects.columns.roomCountValue', { count: project.roomCount })}
                        {' · '}
                        {project.code}
                      </span>
                      {project.inScheduler && (
                        <span className="block text-xs text-muted-foreground">
                          {t('projects.merge.inScheduler')}
                        </span>
                      )}
                    </span>
                    <span
                      className={
                        'inline-flex shrink-0 items-center rounded-xl px-2 py-0.5 text-xs font-medium ' +
                        projectStatusBadgeClass(project.status)
                      }
                    >
                      {projectStatusLabel(t, project.status, project.reofferCount)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        )}

        {data && data.total > data.items.length && (
          <p className="text-xs text-muted-foreground">
            {t('projects.merge.listTruncated', { shown: data.items.length, total: data.total })}
          </p>
        )}
        {!canContinue && (
          <p className="text-sm text-muted-foreground">{t('projects.merge.minimumHint')}</p>
        )}

        <DialogFooter className="mt-0 border-t border-border px-0 pt-4">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={onNext} disabled={!canContinue}>
            {t('projects.merge.submit')}
          </Button>
        </DialogFooter>
      </div>
    </>
  );
}

function DetailsStep({
  projects,
  merge,
  clientDialogOpen,
  onClientDialogOpenChange,
  onBack,
}: {
  projects: ProjectListItem[];
  merge: ReturnType<typeof useMergeProjects>;
  clientDialogOpen: boolean;
  onClientDialogOpenChange: (open: boolean) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientId: projects[0]?.clientId ?? '',
      title: projects.map((project) => project.title).join(' + '),
    },
  });

  const roomQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: roomsQueryKeys.list(project.id),
      queryFn: () => roomsApi.list(project.id),
      meta: { silent: true },
    })),
  });
  const rooms = projects.flatMap((project, index) =>
    (roomQueries[index]?.data ?? []).map((room) => ({ room, project })),
  );
  const roomCount = projects.reduce((sum, project) => sum + project.roomCount, 0);

  const onSubmit = async (values: FormValues) => {
    try {
      const created = await merge.mutateAsync({
        ...values,
        projectIds: projects.map((project) => project.id),
      });
      void navigate({ to: '/projects/$id', params: { id: created.id } });
    } catch {
      /* surfaced via the banner below */
    }
  };

  const onClientCreated = (client: ClientDetails) => {
    form.setValue('clientId', client.id, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <>
      <DialogHeader className="mb-0 border-b border-border px-6 py-5">
        <DialogTitle className="text-xl font-semibold">
          {t('projects.merge.detailsTitle')}
        </DialogTitle>
        <DialogDescription>{t('projects.merge.detailsDescription')}</DialogDescription>
      </DialogHeader>
      <Form form={form} onSubmit={onSubmit} className="space-y-4 px-6 py-5">
        <ClientSearchField
          name="clientId"
          label={t('projects.fields.client')}
          placeholder={t('projects.fields.clientPlaceholder') ?? undefined}
          onCreateNew={() => onClientDialogOpenChange(true)}
        />
        <TextField name="title" label={t('projects.fields.title')} />

        <div>
          <div className="mb-2 text-sm font-semibold">
            {t('projects.merge.rooms', { count: roomCount })}
          </div>
          {roomQueries.some((query) => query.isLoading) ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : roomQueries.some((query) => query.isError) ? (
            <p className="text-sm text-muted-foreground">{t('projects.merge.roomsLoadError')}</p>
          ) : rooms.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('projects.merge.roomsEmpty')}</p>
          ) : (
            <ul className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-border p-2 text-sm">
              {rooms.map(({ room, project }) => (
                <li
                  key={room.id}
                  className="flex items-baseline justify-between gap-3 rounded-lg px-2 py-1"
                >
                  <span className="truncate font-medium">{room.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{project.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
          {t('projects.merge.hint', { days: PROJECT_TRASH_DAYS })}
        </p>

        <FormErrorBanner error={merge.error} resolveKey={errorKeyFor} />

        <DialogFooter className="mt-0 border-t border-border px-0 pt-4">
          <Button type="button" variant="ghost" onClick={onBack} disabled={merge.isPending}>
            {t('projects.merge.back')}
          </Button>
          <Button type="submit" disabled={merge.isPending}>
            {merge.isPending ? t('common.loading') : t('projects.merge.submit')}
          </Button>
        </DialogFooter>
      </Form>
      <ClientCreateDialog
        open={clientDialogOpen}
        onClose={() => onClientDialogOpenChange(false)}
        onCreated={onClientCreated}
      />
    </>
  );
}
